/**
 * Subscription Session Storage Helpers
 * 
 * Utilities for clearing subscription purchase state from session storage.
 * Similar to booking/context/sessionStorage.ts but for subscription flow.
 * 
 * **Important**: Uses browserAdapter.session for iframe-safe sessionStorage.
 * Falls back gracefully if storage is unavailable (parent bridge → memory).
 * 
 * @module subscription/utils
 */

import { useEffect } from 'react';
import { browserAdapter } from '~/services/browserAdapter';

/** Namespace for all subscription-related storage keys */
const STORAGE_PREFIX = 'web-portal-2:subscription';

/**
 * Clear all subscription purchase data from session storage
 * 
 * Removes all keys with the subscription namespace prefix.
 * Used on confirmation page to ensure "Subscribe Again" button starts fresh.
 * 
 * Clears:
 * - Selected subscription (membership or pass)
 * - Selected payment method
 * - Purchase-in-progress flags
 * - Any other subscription purchase flow data
 * 
 * Preserves:
 * - User's active subscriptions list (different namespace)
 * - User authentication state
 * - App configuration
 * 
 * @example
 * ```typescript
 * await clearAllSubscriptionData();
 * console.log('Subscription purchase context cleared');
 * ```
 */
export async function clearAllSubscriptionData(): Promise<void> {
  try {
    // Get all keys from session storage
    const allKeys = await browserAdapter.session.keys();
    
    // Filter keys with subscription namespace prefix
    const keysToRemove = allKeys.filter(key => key.startsWith(STORAGE_PREFIX));
    
    // Remove all matching keys
    await Promise.all(
      keysToRemove.map(key => browserAdapter.session.remove(key))
    );
    
    if (import.meta.env.DEV) {
      console.log(
        `[SubscriptionContext] Cleared ${keysToRemove.length} subscription items from storage`
      );
    }
  } catch (error) {
    // Fail silently - storage may be disabled
    if (import.meta.env.DEV) {
      console.warn(
        '[SubscriptionContext] Failed to clear subscription data from storage',
        error
      );
    }
  }
}

/**
 * Hook to clear subscription context on confirmation page mount
 * 
 * Automatically clears subscription purchase data when the confirmation
 * page loads, ensuring users start with a clean slate if they want to
 * subscribe again.
 * 
 * Similar to booking's clearAllBookingData() pattern.
 * 
 * @example
 * ```typescript
 * function SubscriptionConfirmationPage() {
 *   useClearSubscriptionOnConfirmation();
 *   // ... rest of component
 * }
 * ```
 */
export function useClearSubscriptionOnConfirmation(): void {
  useEffect(() => {
    clearAllSubscriptionData();
    
    if (import.meta.env.DEV) {
      console.log(
        '[SubscriptionConfirmation] Cleared subscription context from sessionStorage'
      );
    }
  }, []); // Empty deps - run once on mount
}
